import { Listbox as HeadlessListbox, Transition } from "@headlessui/react";
import { Fragment, ReactNode, Ref, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { CheckIcon, ChevronDownIcon } from "@talladega/icons";
import { Status, LabelTextStyle } from "@talladega/types";
import { Hint } from "../Hint";
import { Feedback } from "../Feedback";
import { getInputFocusStyle } from "../helpers";

export type ListboxProps<T> = {
  onChange: (selected: T) => void;
  selectedItem?: T | T[];
  items: T[];

  /**
   * Unique key `Option` prop
   */
  keyOption: (item: T) => string | number | null;

  /**
   * Button's display value
   */
  displayButton: (item?: T) => string | number | boolean;

  /**
   * Option's display value
   */
  displayOption: (item: T) => string | number | boolean;

  /**
   * Option's selected value
   */
  valueOption: (item: T) => T | string | number | boolean | null;

  /**
   * Option's disable prop
   */
  disabledOption?: (item: T) => boolean;

  /**
   * Custom render for selected Option
   */
  renderSelected?: (selected?: T) => JSX.Element;

  /**
   * Custom render for Option
   */
  renderOption?: (
    item: T,
    selected?: boolean,
    active?: boolean,
    disabled?: boolean
  ) => JSX.Element;

  label?: string;
  toolTip?: ReactNode;
  labelTextStyle?: LabelTextStyle;
  hint?: string;
  multiple?: boolean;
  name?: string;
  disabled?: boolean;
  leftIcon?: ReactNode;
  dropdownIcon?: ReactNode;
  status?: Status;
  feedback?: ReactNode;
  defaultButtonClassName?: string;
  menuPosition?: "Up" | "Down";
  isOptional?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  itemsTextDisplay?: "truncate" | "full";
  by?: (keyof T & string) | ((a: T, z: T) => boolean);
  hasSelectAllOption?: boolean;
};

export const Listbox = <T,>({
  onChange,
  items,
  selectedItem,
  keyOption,
  displayOption,
  displayButton,
  valueOption,
  disabledOption,
  renderSelected,
  renderOption,
  label,
  toolTip,
  labelTextStyle = "gray",
  hint,
  multiple = false,
  name = "",
  disabled = false,
  leftIcon,
  dropdownIcon,
  status = "default",
  feedback = "",
  menuPosition = "Down",
  defaultButtonClassName = "",
  isOptional = false,
  buttonRef,
  itemsTextDisplay = "truncate",
  by,
  hasSelectAllOption = false,
}: ListboxProps<T>) => {
  const { t } = useTranslation(["common-components"]);
  const [lastKeyPressed, setLastKeyPressed] = useState<string | null>(null);
  const buttonLabelRef = useRef<HTMLSpanElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  const defaultButtonClass =
    !selectedItem || (Array.isArray(selectedItem) && selectedItem.length === 0)
      ? `${defaultButtonClassName}`
      : "";

  const defaultRenderSelected = (selected?: T) => (
    <span className={classNames("block max-w-md truncate", defaultButtonClass)}>
      {displayButton(selected)}&nbsp;
    </span>
  );

  const defaultRenderOption = (item: T, selected?: boolean) => (
    <>
      <span
        className={classNames("block", {
          "font-medium": selected,
          "font-normal": !selected,
          truncate: itemsTextDisplay === "truncate",
        })}
      >
        {displayOption(item)}
      </span>
      {selected ? (
        <span
          className={classNames(
            selected ? "text-white" : "text-primary-500",
            "absolute inset-y-0 right-0 flex items-center pr-4"
          )}
        >
          <CheckIcon className="h-3 w-3 text-success-600" aria-hidden="true" />
        </span>
      ) : null}
    </>
  );

  const statusColor: Record<string, string> = {
    default: "border-primary kijiji:border-other-light-100",
    error: "border-error focus:border-error focus:shadow-error/8",
  };

  const inputClassName = classNames(
    "text-md form-input border text-left rounded-lg w-full focus:outline-none",
    getInputFocusStyle(),
    {
      "bg-background-light-300 disabled:border-other-light-100": disabled,
    },
    {
      "pl-8": leftIcon,
    },
    {
      "pr-8": dropdownIcon,
    },
    {
      "bg-white": !disabled,
    },
    {
      "font-medium": !!selectedItem,
    },
    statusColor[status] || statusColor.default
  );

  const SELECT_ALL_SYMBOL = Symbol("__SELECT_ALL__");

  type SelectAllOption = {
    __isSelectAll: typeof SELECT_ALL_SYMBOL;
    label: string;
  };

  const isSelectAllOption = (
    item: T | SelectAllOption
  ): item is SelectAllOption =>
    typeof item === "object" &&
    item !== null &&
    "__isSelectAll" in item &&
    // eslint-disable-next-line no-underscore-dangle
    (item as SelectAllOption).__isSelectAll === SELECT_ALL_SYMBOL;

  const selectAllItem: SelectAllOption = {
    __isSelectAll: SELECT_ALL_SYMBOL,
    label: "All",
  };

  const handleChange = (
    selected: T | T[] | SelectAllOption | SelectAllOption[]
  ) => {
    const selectedList = Array.isArray(selected) ? selected : [selected];
    const isSelectAll = selectedList.some(isSelectAllOption);

    if (isSelectAll) {
      if (selectedList.length - 1 === items.length) {
        onChange([] as T);
      } else {
        onChange(items as T);
      }
    } else {
      const cleaned = selectedList.filter(
        (item): item is T => !isSelectAllOption(item)
      );
      if (multiple) {
        onChange(cleaned as T);
      } else if (cleaned.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onChange(cleaned[0]!);
      }
    }
  };

  const renderOptionElement = (item: T, selected?: boolean) => {
    if (isSelectAllOption(item)) {
      return <span className="block font-medium truncate">{item.label}</span>;
    }

    return (
      renderOption?.(item as T, selected) ??
      defaultRenderOption(item as T, selected)
    );
  };

  return (
    <div>
      <HeadlessListbox
        value={selectedItem}
        onChange={handleChange}
        multiple={multiple}
        name={name}
        disabled={disabled}
        by={by}
      >
        {({ open }) => (
          <>
            {label && (
              <div className="flex flex-row justify-between">
                <div className="text-sm font-medium flex-initial text-text-light-100">
                  <HeadlessListbox.Label
                    className={classNames(
                      "text-sm font-medium flex-initial basis-1/2 text-secondary",
                      {
                        "text-secondary": labelTextStyle === "gray",
                        "text-gray-50": labelTextStyle === "light-gray",
                      }
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div>
                        {label}
                        <span className="ml-1">{toolTip}</span>
                      </div>
                      {isOptional && (
                        <span className="capitalize italic">{`(${t(
                          "common-components:optional"
                        )})`}</span>
                      )}
                    </div>
                  </HeadlessListbox.Label>
                </div>
              </div>
            )}
            <div className="relative py-1">
              <span className="pointer-events-none absolute inset-y-2 left-0 flex h-8 w-8 items-center pl-2 text-text-light-200">
                {leftIcon}
              </span>
              <HeadlessListbox.Button
                className={classNames(
                  inputClassName,
                  "flex items-center justify-between"
                )}
                onFocus={() => {
                  if (status === "error") {
                    buttonLabelRef.current?.click();
                  }
                }}
                ref={buttonRef}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                  // Open the listbox when letter or number pressed
                  if (e.key.match(/^[a-zA-Z0-9]$/)) {
                    buttonLabelRef.current?.click();
                    setLastKeyPressed(e.key);
                  }
                }}
              >
                <span
                  ref={buttonLabelRef}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  className="flex-grow"
                >
                  {renderSelected
                    ? renderSelected(selectedItem as T)
                    : defaultRenderSelected(selectedItem as T)}
                </span>
                <span className="flex-shrink-0">
                  {dropdownIcon || <ChevronDownIcon />}
                </span>
              </HeadlessListbox.Button>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterEnter={() => {
                  if (lastKeyPressed) {
                    optionsRef.current?.dispatchEvent(
                      new KeyboardEvent("keydown", {
                        key: lastKeyPressed,
                        code: `Key${lastKeyPressed}`,
                        bubbles: true,
                      })
                    );
                  }
                }}
              >
                <HeadlessListbox.Options
                  ref={optionsRef}
                  className={classNames(
                    "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-left shadow-elevation-03 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm",
                    {
                      "-translate-y-[125%]": menuPosition === "Up",
                    }
                  )}
                >
                  {(hasSelectAllOption && items.length > 0
                    ? [selectAllItem, ...items]
                    : items
                  ).map((item) => {
                    let isOptionDisabled = false;

                    if (isSelectAllOption(item)) {
                      isOptionDisabled = false;
                    } else {
                      isOptionDisabled = disabledOption
                        ? disabledOption(item as T)
                        : false;
                    }

                    return (
                      <HeadlessListbox.Option
                        key={
                          isSelectAllOption(item)
                            ? SELECT_ALL_SYMBOL.toString()
                            : keyOption(item as T)
                        }
                        className={({
                          active,
                          disabled: disabledRenderProp,
                          selected,
                        }) =>
                          classNames(
                            "relative cursor-default select-none py-2 pl-3 pr-9",
                            // Neutral state had to be explicitly set because tailwind's arbitrary variants doesn't like to override previously set classes.
                            {
                              "text-gray-900 [&>span]:font-medium":
                                !active && !disabledRenderProp && !selected,
                            },
                            {
                              "bg-background-light-300 text-gray-900 [&>span]:font-medium":
                                active,
                            },
                            {
                              "bg-background-light-300 text-gray-900 [&>span]:font-semibold":
                                selected,
                            },
                            {
                              "italic text-gray-500": disabledRenderProp,
                            }
                          )
                        }
                        disabled={isOptionDisabled}
                        value={valueOption(item as T)}
                      >
                        {({ selected }) =>
                          renderOptionElement(item as T, selected)
                        }
                      </HeadlessListbox.Option>
                    );
                  })}
                </HeadlessListbox.Options>
              </Transition>
              <div className="flex items-start">
                {hint && <Hint hint={hint} align="left" />}
              </div>
            </div>
          </>
        )}
      </HeadlessListbox>
      {feedback && <Feedback status={status}>{feedback}</Feedback>}
    </div>
  );
};
